import { useState } from 'react';

import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';

import { Playground } from '../../../components/playground/Playground';
import {
    PlaygroundClasses,
    PlaygroundResponsiveClasses,
    PlaygroundPrintClasses,
    PlaygroundNegativeClasses,
    PlaygroundClassesSpacer,
} from '../../../components/playground/PlaygroundClasses';
import { Headline } from '../../../components/Headline';
import { Title } from '../../../components/Title';

import { spacings } from '../../../utils/spacings';
const spacingsWithoutZero = spacings.slice(1);
const spacingOptions: SelectOption[] = spacings.map(spacing => ({ id: spacing, label: `${spacing}px` }));
const marginDirections = [
    { label: 'Surrounding', prefix: 'margin-' },
    { label: 'Horizontal', prefix: 'margin-x-' },
    { label: 'Vertical', prefix: 'margin-y-' },
    { label: 'Left', prefix: 'margin-left-' },
    { label: 'Right', prefix: 'margin-right-' },
    { label: 'Top', prefix: 'margin-top-' },
    { label: 'Bottom', prefix: 'margin-bottom-' },
];
const helperChildren = Array.from({ length: 5 }, (_, index) => index);

export const Margin = () => (
    <div>
        <Headline anchor='margin'>Margin</Headline>
        <Playground example={<MarginExample />} classes={marginClasses} />
        <Headline anchor='margin-helper'>Margin helper</Headline>
        <Playground example={marginHelperExample} classes={marginHelperClasses} />
    </div>
);

const MarginExample = () => {
    const [spacing, setSpacing] = useState('20');

    const handleSpacingChange = (option: SelectOption | undefined) => setSpacing(option?.id ?? '20');

    return (
        <div>
            <div className='max-width-300 margin-bottom-25'>
                <Select
                    id='margin-spacing-value'
                    label='Margin'
                    options={spacingOptions}
                    value={[spacing]}
                    onChange={handleSpacingChange}
                />
            </div>
            <div className='display-grid grid-cols-1 grid-cols-2-sm grid-cols-4-lg gap-20'>
                {marginDirections.map(({ label, prefix }) => {
                    const className = `${prefix}${spacing}`;

                    return (
                        <div key={prefix}>
                            <Title>{label}</Title>
                            <div className='display-flex flex-column height-200 padding-15 overflow-hidden rounded bg-lightest'>
                                <div className='display-flex align-items-center justify-content-center flex-1-1'>
                                    <div className='rounded shorthand-style'>
                                        <div className={`${className} width-50 height-50 rounded-inner bg-info`} />
                                    </div>
                                </div>
                                <code className='text-center bg-none'>{className}</code>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const marginClasses = (
    <>
        <PlaygroundClassesSpacer label='Pixel values' />
        <PlaygroundClasses label='Surrounding' prefix='margin-' classes={['auto', ...spacings]} />
        <PlaygroundClasses label='Horizontal' prefix='margin-x-' classes={['auto', ...spacings]} />
        <PlaygroundClasses label='Vertical' prefix='margin-y-' classes={['auto', ...spacings]} />
        <PlaygroundClasses label='Left' prefix='margin-left-' classes={['auto', ...spacings]} />
        <PlaygroundClasses label='Right' prefix='margin-right-' classes={['auto', ...spacings]} />
        <PlaygroundClasses label='Top' prefix='margin-top-' classes={['auto', ...spacings]} />
        <PlaygroundClasses label='Bottom' prefix='margin-bottom-' classes={['auto', ...spacings]} />
        <PlaygroundClassesSpacer label='Percentage values' />
        <PlaygroundClasses label='Surrounding' prefix='margin-' classes={spacingsWithoutZero} suffix='pct' />
        <PlaygroundClasses label='Horizontal' prefix='margin-x-' classes={spacingsWithoutZero} suffix='pct' />
        <PlaygroundClasses label='Vertical' prefix='margin-y-' classes={spacingsWithoutZero} suffix='pct' />
        <PlaygroundClasses label='Left' prefix='margin-left-' classes={spacingsWithoutZero} suffix='pct' />
        <PlaygroundClasses label='Right' prefix='margin-right-' classes={spacingsWithoutZero} suffix='pct' />
        <PlaygroundClasses label='Top' prefix='margin-top-' classes={spacingsWithoutZero} suffix='pct' />
        <PlaygroundClasses label='Bottom' prefix='margin-bottom-' classes={spacingsWithoutZero} suffix='pct' />
        <PlaygroundNegativeClasses />
        <PlaygroundResponsiveClasses />
        <PlaygroundPrintClasses />
    </>
);

const marginHelperExample = (
    <div className='display-grid grid-cols-1 grid-cols-4-sm gap-20'>
        <div>
            <Title>Last child (self class)</Title>
            <div>
                {helperChildren.map(index => (
                    <div
                        className={`margin-bottom-10 padding-2 text-center rounded ${
                            index === helperChildren.length - 1 ? 'bg-success last-child-margin-bottom-0' : 'bg-info'
                        }`}
                        key={index}
                    >
                        {index === helperChildren.length - 1 ? 'Last child' : 'Child'}
                    </div>
                ))}
            </div>
        </div>
        <div>
            <Title>Last child (parent class)</Title>
            <div className='children-last-margin-bottom-0'>
                {helperChildren.map(index => (
                    <div
                        className={`margin-bottom-10 padding-2 text-center rounded ${
                            index === helperChildren.length - 1 ? 'bg-success' : 'bg-info'
                        }`}
                        key={index}
                    >
                        {index === helperChildren.length - 1 ? 'Last child' : 'Child'}
                    </div>
                ))}
            </div>
        </div>
        <div>
            <Title>First child (self class)</Title>
            <div>
                {helperChildren.map(index => (
                    <div
                        className={`margin-top-10 first-child-margin-top-0 padding-2 text-center rounded ${
                            index === 0 ? 'bg-success' : 'bg-info'
                        }`}
                        key={index}
                    >
                        {index === 0 ? 'First child' : 'Child'}
                    </div>
                ))}
            </div>
        </div>
        <div>
            <Title>First child (parent class)</Title>
            <div className='children-first-margin-top-0'>
                {helperChildren.map(index => (
                    <div
                        className={`margin-top-10 padding-2 text-center rounded ${
                            index === 0 ? 'bg-success' : 'bg-info'
                        }`}
                        key={index}
                    >
                        {index === 0 ? 'First child' : 'Child'}
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const marginHelperClasses = (
    <>
        <PlaygroundClasses label='Parent' classes={['children-first-margin-top-0', 'children-last-margin-bottom-0']} />
        <PlaygroundClasses label='Self' classes={['first-child-margin-top-0', 'last-child-margin-bottom-0']} />
    </>
);
